/**
 * Goodwill points adjustment dialog -- phase 6b, Task 4.
 *
 * `POST /admin/discounts/customers/:id/points-adjustment` -- rendered only
 * when the caller has already gated it behind `useCanWriteDiscounts()`
 * (CustomerOverview does this; the API would 403 anyway, this just avoids
 * showing a button that would).
 *
 * Validation mirrors `pointsAdjustmentBody` (types.ts): points is a
 * non-zero integer, |points| <= 1000; note is 3-200 chars trimmed. Save
 * stays disabled until both hold. The live preview reads the *current*
 * balance passed in by the caller and does plain integer arithmetic --
 * points are counts, not money, so this is not a money.ts concern.
 *
 * A positive delta can auto-issue a reward (`rewardsIssued`); the success
 * state below always surfaces `balanceBefore -> balanceAfter` and, when
 * present, the issued codes (ADMIN_API.md "Goodwill points adjustment can
 * auto-issue a reward" -- silently dropping `rewardsIssued` is a real bug,
 * not a cosmetic omission, since it is the only place an admin sees the
 * reward that was just granted). A 422 (insufficient balance, no loyalty
 * card yet, etc.) shows its `userMessage` verbatim under the form via
 * `toApiFailure`.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { discountsApi } from '../api';
import { toApiFailure } from '../errors';
import type { PointsAdjustmentResponse } from '../types';

export type AdjustmentSuccess = { balanceBefore: number; balanceAfter: number; rewardsIssued: string[] };

type Props = {
  open: boolean;
  onClose: () => void;
  customerId: string;
  currentBalance: number;
  onSuccess: (result: AdjustmentSuccess) => void;
};

const MAX_ABS_POINTS = 1000;

/** Whole number only (no `.`, no scientific notation) -- matches the int Zod field. */
const parsePoints = (raw: string): number | null => {
  const s = raw.trim();
  if (!/^-?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
};

export const PointsAdjustmentDialog: React.FC<Props> = ({ open, onClose, customerId, currentBalance, onSuccess }) => {
  const [pointsRaw, setPointsRaw] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<PointsAdjustmentResponse | null>(null);

  const points = parsePoints(pointsRaw);
  const trimmedNote = note.trim();
  const pointsValid = points !== null && points !== 0 && Math.abs(points) <= MAX_ABS_POINTS;
  const noteValid = trimmedNote.length >= 3 && trimmedNote.length <= 200;
  const canSave = pointsValid && noteValid && !submitting;

  const reset = () => {
    setPointsRaw('');
    setNote('');
    setMessage(null);
    setResult(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!canSave || points === null) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const response = await discountsApi.adjustPoints(customerId, { points, note: trimmedNote });
      setResult(response);
      onSuccess({
        balanceBefore: response.balanceBefore,
        balanceAfter: response.balanceAfter,
        rewardsIssued: response.rewardsIssued,
      });
    } catch (err) {
      setMessage(toApiFailure(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Adjust points</DialogTitle>
      <DialogContent>
        {result ? (
          <Box sx={{ py: 1 }}>
            <Alert severity="success" sx={{ mb: 1 }}>
              Balance {result.balanceBefore} → {result.balanceAfter}
            </Alert>
            {result.rewardsIssued.length > 0 && (
              <Typography variant="body2">
                Reward{result.rewardsIssued.length > 1 ? 's' : ''} issued: {result.rewardsIssued.join(', ')}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Points (+ to add, − to remove)"
              value={pointsRaw}
              onChange={(e) => setPointsRaw(e.target.value)}
              helperText="Whole number, non-zero, up to ±1000"
              error={pointsRaw !== '' && !pointsValid}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              helperText={`${trimmedNote.length}/200 — required for the audit trail (3-200 characters)`}
              error={note !== '' && !noteValid}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Balance {currentBalance} → {points !== null ? currentBalance + points : currentBalance}
            </Typography>
            {message && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {message}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PointsAdjustmentDialog;
